import React, { FC, useRef, useState } from 'react';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import {
    ExtendedManageSubscriptionTableIndividualCell,
    ManageSubscriptionTableCell,
    ManageSubscriptionUsageLimitNone,
    ManageSubscriptionUsageLimitValue
} from '../../utils/subscriptionTableTileTypes';
import { IssueUsageStatisticsCell } from '../../../../shared/subscription-management/IssueUsageStatisticsCell';
import { IssueUsage } from '../../../../shared/subscription-management/types/IssueUsage';
import { StockDetails } from '../../../../store/api';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../../store/loanFormat/actions';
import { ProductCopyStockDropdown } from '../../../../shared/product/ProductCopyStock';
import { Trans, useTranslation } from 'react-i18next';

export type ExtendedCurrentYearSubscriptionUsageLimitValue = {
    type: ExtendedManageSubscriptionTableIndividualCell.UsageLimitValue;
    label: string;
    onAddCopy: () => void;
    stockDetails?: StockDetails;
};
export type ExtendedCurrentYearSubscriptionUsageLimitUnlimited = {
    type: ExtendedManageSubscriptionTableIndividualCell.UsageLimitUnlimited;
    label: string;
    stockDetails?: StockDetails;
};
export type ExtendedCurrentYearSubscriptionUsageLimitNone = {
    type: ExtendedManageSubscriptionTableIndividualCell.UsageLimitNone;
    label: string;
    onAddCopy: () => void;
};
export type ExtendedCurrentYearSubscriptionExpired = {
    type: ExtendedManageSubscriptionTableIndividualCell.Expired;
    onAddCopy: () => void;
    onExpireIssues: () => void;
    expirationDate: string | undefined;
};

export type ExtendedManageSubscriptionIndividual = {
    currentYearSubscription:
        | ExtendedCurrentYearSubscriptionUsageLimitValue
        | ExtendedCurrentYearSubscriptionUsageLimitUnlimited
        | ExtendedCurrentYearSubscriptionUsageLimitNone
        | ExtendedCurrentYearSubscriptionExpired;
    nextYearSubscription: ManageSubscriptionUsageLimitValue | ManageSubscriptionUsageLimitNone;
    previousIssueUsage?: IssueUsage;
    currentIssueUsage?: IssueUsage;
};

export const ExtendedManageSubscriptionIndividualRow: FC<ExtendedManageSubscriptionIndividual> = props => {
    const { currentYearSubscription, nextYearSubscription, previousIssueUsage, currentIssueUsage } = props;
    const { t } = useTranslation();
    return (
        <tr className="individual-row">
            <td></td>
            <td>
                <p>
                    <b>{t('shop.subscriptions.individual')}</b>
                </p>
            </td>
            <td className="current-year-subscriptions">
                {(() => {
                    switch (currentYearSubscription.type) {
                        case ExtendedManageSubscriptionTableIndividualCell.UsageLimitValue:
                            return <ExtendedCurrentYearSubscriptionUsageLimit {...currentYearSubscription} />;
                        case ExtendedManageSubscriptionTableIndividualCell.UsageLimitUnlimited:
                            return (
                                <ExtendedUsageLimit
                                    label={currentYearSubscription.label}
                                    stockDetails={currentYearSubscription.stockDetails}
                                />
                            );
                        case ExtendedManageSubscriptionTableIndividualCell.UsageLimitNone:
                            return <ExtendedNoCurrentYearSubscriptionLabel {...currentYearSubscription} />;
                        case ExtendedManageSubscriptionTableIndividualCell.Expired:
                            return <ExtendedExpiredCurrentYearSubscriptionLabel {...currentYearSubscription} />;
                        default:
                            return <></>;
                    }
                })()}
            </td>
            <td className="previous-issue-usage">
                {previousIssueUsage ? <IssueUsageStatisticsCell {...previousIssueUsage} /> : null}
            </td>
            <td className="current-issue-usage">
                {currentIssueUsage ? <IssueUsageStatisticsCell {...currentIssueUsage} /> : null}
            </td>
            <td className="next-year-subscriptions">
                {(() => {
                    switch (nextYearSubscription.type) {
                        case ManageSubscriptionTableCell.UsageLimitValue:
                            return (
                                <ExtendedUsageLimit
                                    label={nextYearSubscription.label}
                                    stockDetails={nextYearSubscription.stockDetails}
                                />
                            );
                        case ManageSubscriptionTableCell.UsageLimitNone:
                            return <small>{nextYearSubscription.label}</small>;
                        default:
                            return <></>;
                    }
                })()}
            </td>
        </tr>
    );
};

const ExtendedUsageLimit: FC<{ label: string; stockDetails?: StockDetails }> = ({ label, stockDetails }) => {
    const ref = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    return (
        <>
            <b ref={ref} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
                {label}
            </b>
            <ProductCopyStockDropdown
                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                stockDetails={stockDetails}
                placement="top-start"
                visible={visible}
                popoverRef={ref}
            />
        </>
    );
};

const ExtendedCurrentYearSubscriptionUsageLimit: FC<ExtendedCurrentYearSubscriptionUsageLimitValue> = ({
    label,
    onAddCopy,
    stockDetails
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const { t } = useTranslation();
    return (
        <>
            <div ref={ref} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
                <b>{label}</b>
            </div>
            <Button
                text={t('shop.subscriptions.addIndividualCopy')}
                onClick={onAddCopy}
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.NEUTRAL}
                icon="shopping_cart"
            />
            <ProductCopyStockDropdown
                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                stockDetails={stockDetails}
                placement="top-start"
                visible={visible}
                popoverRef={ref}
            />
        </>
    );
};

const ExtendedNoCurrentYearSubscriptionLabel: FC<ExtendedCurrentYearSubscriptionUsageLimitNone> = ({
    label,
    onAddCopy
}) => {
    const { t } = useTranslation();
    return (
        <>
            <div>
                <small>{label}</small>
            </div>
            <Button
                text={t('shop.subscriptions.addIndividualCopy')}
                onClick={onAddCopy}
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.NEUTRAL}
                icon="shopping_cart"
            />
        </>
    );
};

const ExtendedExpiredCurrentYearSubscriptionLabel: FC<ExtendedCurrentYearSubscriptionExpired> = ({
    onAddCopy,
    onExpireIssues,
    expirationDate
}) => {
    const { t } = useTranslation();
    return (
        <>
            <div>
                <Trans
                    i18nKey="shop.subscriptions.expired"
                    components={{ boldRed: <b className="red" />, small: <small />, b: <b /> }}
                    values={{
                        date: t('dateFormat.dateWithMonth', {
                            val: new Date(expirationDate ?? '')
                        })
                    }}
                />
            </div>
            <div>
                <Button
                    text={t('shop.subscriptions.addIndividualCopy')}
                    onClick={onAddCopy}
                    size={ButtonSize.MEDIUM}
                    theme={ButtonTheme.NEUTRAL}
                    icon="shopping_cart"
                />
                <Button
                    className="expireIssues"
                    text={t('expireIssuesNow')}
                    onClick={onExpireIssues}
                    size={ButtonSize.MEDIUM}
                    theme={ButtonTheme.DANGER_INVERT}
                    icon="delete_forever"
                />
            </div>
        </>
    );
};
